(() => {
  const DESKTOP_QUERY = '(min-width: 990px)';
  const PRECISE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

  const parseDelay = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (number == null || Number.isNaN(number)) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? parts.join(decimal) : parts[0];
  };

  const formatMoney = (cents, format) => {
    const moneyFormat = format || '${{amount}}';
    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(cents);

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount':
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    return moneyFormat.replace(match[0], value);
  };

  const closeAll = (exceptItem = null) => {
    document.querySelectorAll('[data-ns-mega-item].is-open').forEach((item) => {
      if (item === exceptItem) return;
      item.nsCloseMega?.();
    });

    document.documentElement.classList.toggle(
      'ns-mega-menu-open',
      Boolean(document.querySelector('[data-ns-mega-item].is-open'))
    );
  };

  const activateTab = (mega, panelId, moveFocus = false) => {
    const tabs = Array.from(mega.querySelectorAll('[data-ns-mega-tab]'));
    const panels = Array.from(mega.querySelectorAll('[data-ns-mega-products-panel]'));

    tabs.forEach((tab) => {
      const active = tab.dataset.nsMegaTab === panelId;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      if (active && moveFocus) tab.focus();
    });

    panels.forEach((panel) => {
      const active = panel.id === panelId;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });
  };

  const initializeMegaItem = (item) => {
    if (item.dataset.nsMegaReady === 'true') return;

    const trigger = item.querySelector(':scope > [data-ns-mega-trigger]');
    const mega = item.querySelector(':scope > [data-ns-mega-panel]');
    if (!trigger || !mega) return;

    item.dataset.nsMegaReady = 'true';

    const mode = mega.dataset.openMode || 'hover_click';
    const hoverEnabled = mode === 'hover' || mode === 'hover_click';
    const clickEnabled = mode === 'click' || mode === 'hover_click';
    const openDelay = parseDelay(mega.dataset.openDelay, 100);
    const closeDelay = parseDelay(mega.dataset.closeDelay, 450);
    let openTimer;
    let closeTimer;

    const setOpen = (open) => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);

      if (open) {
        closeAll(item);
        item.closest('[data-ns-header-root]')?.querySelectorAll('.ns-nav-dropdown[open]').forEach((details) => {
          details.removeAttribute('open');
        });
      }

      item.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      mega.setAttribute('aria-hidden', open ? 'false' : 'true');
      mega.hidden = !open;

      document.documentElement.classList.toggle(
        'ns-mega-menu-open',
        Boolean(document.querySelector('[data-ns-mega-item].is-open'))
      );
    };

    const scheduleOpen = () => {
      window.clearTimeout(closeTimer);
      if (item.classList.contains('is-open')) return;
      openTimer = window.setTimeout(() => setOpen(true), openDelay);
    };

    const scheduleClose = () => {
      window.clearTimeout(openTimer);
      closeTimer = window.setTimeout(() => setOpen(false), closeDelay);
    };

    item.nsCloseMega = () => setOpen(false);

    const canHover = () =>
      window.matchMedia(DESKTOP_QUERY).matches &&
      window.matchMedia(PRECISE_POINTER_QUERY).matches;

    if (hoverEnabled) {
      item.addEventListener('mouseenter', () => {
        if (canHover()) scheduleOpen();
      });

      item.addEventListener('mouseleave', () => {
        if (canHover()) scheduleClose();
      });

      mega.addEventListener('mouseenter', () => window.clearTimeout(closeTimer));
    }

    trigger.addEventListener('click', () => {
      if (!clickEnabled && canHover()) {
        setOpen(true);
        return;
      }
      setOpen(!item.classList.contains('is-open'));
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
        mega.querySelector('[data-ns-mega-tab]')?.focus();
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    });

    item.addEventListener('focusin', () => window.clearTimeout(closeTimer));
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) scheduleClose();
    });

    mega.querySelectorAll('[data-ns-mega-tab]').forEach((tab, index, tabsNodeList) => {
      const tabs = Array.from(tabsNodeList);
      const openTab = () => activateTab(mega, tab.dataset.nsMegaTab);

      tab.addEventListener('mouseenter', openTab);
      tab.addEventListener('focus', openTab);
      tab.addEventListener('click', openTab);

      tab.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        activateTab(mega, tabs[nextIndex].dataset.nsMegaTab, true);
      });
    });

    mega.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      trigger.focus();
    });
  };

  const enhanceCartSummary = (root) => {
    const cartLink = root.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.nsCartReady === 'true') return;
    cartLink.dataset.nsCartReady = 'true';

    let refreshing = false;
    const refresh = async () => {
      if (refreshing || cartLink.querySelector('[data-ns-cart-summary]')) return;
      refreshing = true;

      try {
        const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
        const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unable to refresh cart summary');

        const cart = await response.json();
        const summary = document.createElement('span');
        summary.className = 'ns-header-cart__summary';
        summary.dataset.nsCartSummary = '';
        summary.textContent = `${formatMoney(cart.total_price, cartLink.dataset.moneyFormat)} (${cart.item_count})`;
        cartLink.appendChild(summary);

        root.querySelectorAll('[data-ns-mobile-cart-count]').forEach((count) => {
          count.textContent = cart.item_count;
          count.hidden = cart.item_count < 1;
        });
      } catch (error) {
        console.warn('[Reference header]', error);
      } finally {
        refreshing = false;
      }
    };

    const observer = new MutationObserver(() => {
      if (!cartLink.querySelector('[data-ns-cart-summary]')) refresh();
    });
    observer.observe(cartLink, { childList: true, subtree: true });
  };

  const initializeHeader = (root) => {
    if (!root || root.dataset.nsHeaderReady === 'true') return;
    root.dataset.nsHeaderReady = 'true';

    root.querySelectorAll('[data-ns-mega-item]').forEach(initializeMegaItem);
    enhanceCartSummary(root);

    root.querySelectorAll('.ns-nav-dropdown').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (details.open) closeAll();
      });
    });
  };

  document.addEventListener('click', (event) => {
    const openItem = document.querySelector('[data-ns-mega-item].is-open');
    if (openItem && !openItem.contains(event.target)) closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeAll();
  });

  window.addEventListener('resize', () => {
    if (!window.matchMedia(DESKTOP_QUERY).matches) closeAll();
  });

  const boot = (scope = document) => {
    scope.querySelectorAll?.('[data-ns-header-root]').forEach(initializeHeader);
    if (scope.matches?.('[data-ns-header-root]')) initializeHeader(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', () => closeAll());
  document.addEventListener('ns:close-mega-menus', () => closeAll());
})();
